import dayjs from "dayjs";
import type React from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { EventDateSection } from "@/components/common/EventDateSection";
import { RecurrenceEditor } from "@/components/common/RecurrenceEditor";
import { TimeZoneFieldWithModal } from "@/components/common/TimeZoneFieldWithModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_LOCATION_MAX_LENGTH,
  EVENT_LOCATION_TYPE_HYBRID,
  EVENT_LOCATION_TYPE_OFFLINE,
  EVENT_LOCATION_TYPE_ONLINE,
  EVENT_ONLINE_LOCATION_MAX_LENGTH,
  EVENT_TITLE_MAX_LENGTH,
  LANG_EN,
  LANG_JP,
  STUDYGROUP_VISIBILITY_SEARCH_DISABLED,
} from "@/constants";
import type { EventFormData } from "@/features/study-group-events/type";
import { usePropagateReminders } from "@/hooks/usePropagateReminders";
import { useRecomposeEvent } from "@/hooks/useRecomposeEvent";
import { useUpdateEvent } from "@/hooks/useUpdateEvent";
import type {
  EventApiResponse,
  RecurrenceRule,
  RecurrenceScope,
} from "@/lib/humhub-api/types";
import { buildInitialFormDataFromApiEvent } from "@/lib/utils/event/form-initializer";
import {
  dayjsWeekdayToRecurrence,
  formatRecurrencePattern,
  getMonthlyWeekInfo,
} from "@/lib/utils/event/recurrence";
import { buildEventUpdateDiff } from "@/lib/utils/event/update-diff";
import {
  getDateTimeValidationMessage,
  validateDateTimeOrder,
} from "@/lib/utils/eventUtils";
import { convertTimezoneToTimezone } from "@/lib/utils/dateUtils";
import {
  buildSortedTimeZoneOptions,
  getCurrentBrowserTimeZone,
} from "@/lib/utils/timeZone";
import { ScopeSelectionContent } from "./ScopeSelectionModal";

type ValidationErrors = {
  title?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  location?: string;
  language?: string;
  is_public?: string;
  dateTime?: string;
};

const areRecurrenceRulesEqual = (
  prev?: RecurrenceRule | null,
  next?: RecurrenceRule | null,
): boolean => JSON.stringify(prev ?? null) === JSON.stringify(next ?? null);

const updateWeeklyRecurrence = (
  rule: RecurrenceRule,
  nextStartDate: dayjs.Dayjs,
) => {
  const weekday = dayjsWeekdayToRecurrence(nextStartDate.day());
  return {
    ...rule,
    byDay: weekday,
    byMonthDay: undefined,
    bySetPos: undefined,
  };
};

const updateMonthlyRecurrence = (
  rule: RecurrenceRule,
  nextStartDate: dayjs.Dayjs,
) => {
  const weekday = dayjsWeekdayToRecurrence(nextStartDate.day());
  if (rule.byDay || rule.bySetPos !== undefined) {
    const { weekIndex, isLast } = getMonthlyWeekInfo(nextStartDate);
    return {
      ...rule,
      byDay: weekday,
      bySetPos: isLast ? -1 : weekIndex,
      byMonthDay: undefined,
    };
  }
  return {
    ...rule,
    byMonthDay: nextStartDate.date(),
    byDay: undefined,
    bySetPos: undefined,
  };
};

const clearRecurrenceAnchors = (rule: RecurrenceRule) => ({
  ...rule,
  byDay: undefined,
  byMonthDay: undefined,
  bySetPos: undefined,
});

interface EventEditContentProps {
  eventData: EventApiResponse;
  onSaveSuccess: (options?: { resetModal?: boolean }) => void;
  onCancel: () => void;
}

// initial form builder moved to shared util

export const EventEditContent: React.FC<EventEditContentProps> = ({
  eventData,
  onSaveSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const titleId = useId();

  // eventData.containerから可視性を取得
  const isPrivateGroup =
    eventData?.container?.visibility === STUDYGROUP_VISIBILITY_SEARCH_DISABLED;
  const descriptionId = useId();
  const inpersonLocationId = useId();
  const onlineLocationId = useId();
  const isPublicId = useId();
  const languageId = useId();
  const recurrenceToggleId = useId();
  const [formData, setFormData] = useState<EventFormData>(() =>
    buildInitialFormDataFromApiEvent(eventData, {
      preserveEventTimezoneDateTime: true,
    }),
  );
  const initialFormDataRef = useRef<EventFormData>(
    buildInitialFormDataFromApiEvent(eventData, {
      preserveEventTimezoneDateTime: true,
    }),
  );
  const getRecurrenceUiSignature = useCallback(
    (rule?: RecurrenceRule | null, startDateOverride?: string) => {
      if (!rule) return null;
      const startDateObj = dayjs(startDateOverride ?? formData.startDate);
      const until =
        rule.until && dayjs(rule.until).isValid()
          ? dayjs(rule.until).format("YYYY-MM-DD")
          : (rule.until ?? null);
      if (!startDateObj.isValid()) {
        return { freq: rule.freq, until };
      }

      if (rule.freq === "MONTHLY") {
        const usesWeekdayMode = Boolean(
          rule.byDay || rule.bySetPos !== undefined,
        );
        const { isLast } = getMonthlyWeekInfo(startDateObj);
        const weekday = dayjsWeekdayToRecurrence(startDateObj.day());
        const monthlyPattern = usesWeekdayMode
          ? rule.bySetPos === -1 && isLast
            ? "last"
            : "setpos"
          : "date";
        return {
          freq: rule.freq,
          monthlyPattern,
          weekday: usesWeekdayMode ? weekday : undefined,
          monthDay: usesWeekdayMode ? undefined : startDateObj.date(),
          until,
        };
      }

      if (rule.freq === "WEEKLY") {
        const weekday = dayjsWeekdayToRecurrence(startDateObj.day());
        return { freq: rule.freq, weekday, until };
      }

      return { freq: rule.freq, until };
    },
    [formData.startDate],
  );
  const isDirtyField = useCallback(
    (key: keyof EventFormData) => {
      const prev = initialFormDataRef.current[key];
      const next = formData[key];
      if (key === "recurrence") {
        const prevSignature = getRecurrenceUiSignature(
          prev as RecurrenceRule | null,
          initialFormDataRef.current.startDate,
        );
        const nextSignature = getRecurrenceUiSignature(
          next as RecurrenceRule | null,
          formData.startDate,
        );
        return JSON.stringify(prevSignature) !== JSON.stringify(nextSignature);
      }
      if (typeof prev === "object" || typeof next === "object") {
        return JSON.stringify(prev ?? null) !== JSON.stringify(next ?? null);
      }
      return prev !== next;
    },
    [formData, getRecurrenceUiSignature],
  );
  const dirtyClass = useCallback(
    (key: keyof EventFormData) =>
      isDirtyField(key) ? "ring-2 ring-teal-200 bg-teal-50" : "",
    [isDirtyField],
  );

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const browserTimeZone = useMemo(() => getCurrentBrowserTimeZone(), []);
  const timeZoneOptions = useMemo(
    () =>
      buildSortedTimeZoneOptions(
        formData.timeZone || eventData.time_zone || browserTimeZone || "UTC",
      ),
    [browserTimeZone, eventData.time_zone, formData.timeZone],
  );
  const selectedTimeZone =
    formData.timeZone || eventData.time_zone || browserTimeZone || "UTC";

  const { recomposeEvent, isLoading: recomposeLoading } = useRecomposeEvent();
  const { propagateReminders } = usePropagateReminders();

  const {
    updateEvent,
    isLoading: updateLoading,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateEvent();

  const isDraft = eventData?.is_draft === 1;
  const wasRecurringInitially = Boolean(initialFormDataRef.current.recurrence);
  const isMutating = updateLoading || recomposeLoading;
  const isRecurrenceParent = eventData.is_parent === true;
  const requiresScopeSelection = wasRecurringInitially || isRecurrenceParent;
  const [isScopeModalOpen, setIsScopeModalOpen] = useState(false);
  const [pendingScopeAction, setPendingScopeAction] = useState<
    "save" | "backToDraft" | "publish" | null
  >(null);
  const [pendingRecomposeMode, setPendingRecomposeMode] = useState<
    "until_extend" | "until_shrink" | null
  >(null);
  const recurrenceRootStartDate = useMemo(() => {
    const root = eventData?.recurrence_root_start_datetime;
    if (!root) return undefined;
    const parsed = dayjs(root);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
  }, [eventData?.recurrence_root_start_datetime]);
  const minRecurrenceUntilDate = useMemo(() => {
    const root = recurrenceRootStartDate;
    if (root && dayjs(root).isValid()) return root;
    if (!formData.startDate) return undefined;
    return dayjs(formData.startDate).isValid() ? formData.startDate : undefined;
  }, [formData.startDate, recurrenceRootStartDate]);
  const currentDiff = useMemo(() => {
    try {
      return buildEventUpdateDiff(
        initialFormDataRef.current,
        formData,
        eventData.time_zone,
      );
    } catch (error) {
      console.warn("Failed to build diff for current form", error);
      return null;
    }
  }, [formData, eventData.time_zone]);

  const handleDateSectionChange = (nextData: EventFormData) => {
    setFormData((prev) => {
      const prevStart = prev.startDate;
      const nextStart = nextData.startDate;
      let nextRecurrence = nextData.recurrence ?? prev.recurrence;

      if (prevStart !== nextStart && nextRecurrence) {
        const nextStartDate = dayjs(nextStart);
        if (nextStartDate.isValid()) {
          if (nextRecurrence.freq === "WEEKLY") {
            nextRecurrence = updateWeeklyRecurrence(
              nextRecurrence,
              nextStartDate,
            );
          } else if (nextRecurrence.freq === "MONTHLY") {
            nextRecurrence = updateMonthlyRecurrence(
              nextRecurrence,
              nextStartDate,
            );
          } else {
            nextRecurrence = clearRecurrenceAnchors(nextRecurrence);
          }
        }
      }

      if (
        prevStart !== nextStart &&
        prev.recurrence?.until &&
        nextRecurrence?.until
      ) {
        const nextStartDate = dayjs(nextStart);
        const untilDate = dayjs(nextRecurrence.until);
        if (nextStartDate.isValid() && untilDate.isValid()) {
          if (untilDate.isBefore(nextStartDate, "day")) {
            nextRecurrence = {
              ...nextRecurrence,
              until: nextStartDate.format("YYYY-MM-DD"),
            };
          }
        }
      }

      if (prevStart !== nextStart && nextRecurrence?.until) {
        const minDate =
          nextData.startDate && dayjs(nextData.startDate).isValid()
            ? dayjs(nextData.startDate)
            : null;
        const untilDate = dayjs(nextRecurrence.until);
        if (
          minDate &&
          untilDate.isValid() &&
          untilDate.isBefore(minDate, "day")
        ) {
          nextRecurrence = {
            ...nextRecurrence,
            until: minDate.format("YYYY-MM-DD"),
          };
        }
      }

      return { ...prev, ...nextData, recurrence: nextRecurrence };
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: 必要なタイミングのみ再実行
  useEffect(() => {
    setValidationErrors({});
  }, [formData]);

  const handleFormChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= EVENT_TITLE_MAX_LENGTH) {
      handleFormChange("title", value);
    }
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= EVENT_DESCRIPTION_MAX_LENGTH) {
      handleFormChange("description", value);
    }
  };

  const hasRecurrenceChanged = useCallback(
    (targetFormData: EventFormData) =>
      !areRecurrenceRulesEqual(
        initialFormDataRef.current.recurrence,
        targetFormData.recurrence,
      ),
    [],
  );

  const handleLocationTypeChange = (type: number) => {
    setFormData((prev) => ({
      ...prev,
      location_type: type,
      // オフラインの場合はオンラインURLをクリアする
      online_location:
        type === EVENT_LOCATION_TYPE_OFFLINE ? "" : prev.online_location,
      // オンラインの場合は対面の場所をクリアする
      location: type === EVENT_LOCATION_TYPE_ONLINE ? "" : prev.location,
    }));
  };

  const handleOnlineLocationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= EVENT_ONLINE_LOCATION_MAX_LENGTH) {
      handleFormChange("online_location", value);
    }
  };

  const handleInPersonLocationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= EVENT_LOCATION_MAX_LENGTH) {
      handleFormChange("location", value);
    }
  };

  const handleTimeZoneChange = (nextTimeZone: string) => {
    if (nextTimeZone === selectedTimeZone) return;

    if (formData.isAllDay || !formData.startTime || !formData.endTime) {
      handleFormChange("timeZone", nextTimeZone);
      return;
    }

    const nextStart = convertTimezoneToTimezone(
      formData.startDate,
      formData.startTime,
      selectedTimeZone,
      nextTimeZone,
    );
    const nextEnd = convertTimezoneToTimezone(
      formData.endDate,
      formData.endTime,
      selectedTimeZone,
      nextTimeZone,
    );

    handleDateSectionChange({
      ...formData,
      timeZone: nextTimeZone,
      startDate: nextStart.date,
      startTime: nextStart.time,
      endDate: nextEnd.date,
      endTime: nextEnd.time,
    });
  };

  const validateForm = (): { isValid: boolean; errors: ValidationErrors } => {
    const errors: ValidationErrors = {};

    if (!formData.title.trim()) {
      errors.title = t("eventEdit.titleRequired");
    }

    if (!formData.startDate) {
      errors.startDate = t("eventEdit.startDateRequired");
    }

    if (!formData.endDate) {
      errors.endDate = t("eventEdit.endDateRequired");
    }

    // 終日でない場合のみ時刻を必須とする
    if (!formData.isAllDay && !formData.startTime) {
      errors.startTime = t("eventEdit.startTimeRequired");
    }

    if (!formData.isAllDay && !formData.endTime) {
      errors.endTime = t("eventEdit.endTimeRequired");
    }

    // 終日でない場合のみ日時順序バリデーションを行う
    // ↑ 終日の場合は日付だけを確認した方が良さそう
    if (
      !formData.isAllDay &&
      formData.startDate &&
      formData.startTime &&
      formData.endDate &&
      formData.endTime
    ) {
      const validation = validateDateTimeOrder(
        formData.startDate,
        formData.startTime,
        formData.endDate,
        formData.endTime,
      );

      if (!validation.isValid && validation.errorType) {
        errors.dateTime = getDateTimeValidationMessage(validation.errorType, t);
      }
    }

    if (formData.is_public === undefined) {
      errors.is_public = t("eventEdit.visibilityRequired");
    }

    if (!formData.language) {
      errors.language = t("eventEdit.languageRequired");
    }

    if (!isDraft) {
      if (formData.location_type === EVENT_LOCATION_TYPE_HYBRID) {
        if (!formData.location?.trim()) {
          errors.location = t("eventEdit.locationRequired");
        }
      } else if (
        formData.location_type === EVENT_LOCATION_TYPE_OFFLINE &&
        !formData.location?.trim()
      ) {
        errors.location = t("eventEdit.locationRequired");
      }
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const recurrenceOnlyChange = useMemo(() => {
    const recurrenceChanged = hasRecurrenceChanged(formData);
    if (!recurrenceChanged) return false;
    const { CalendarEntryForm, CalendarEntry, ...restDiff } = currentDiff ?? {};
    const formWithoutRecurrence =
      CalendarEntryForm && typeof CalendarEntryForm === "object"
        ? (() => {
            const {
              recurrence: _recurrence,
              scope: _scope,
              ...restForm
            } = CalendarEntryForm;
            return restForm;
          })()
        : undefined;
    const hasNonRecurrenceChanges =
      (CalendarEntry && Object.keys(CalendarEntry).length > 0) ||
      (formWithoutRecurrence &&
        Object.keys(formWithoutRecurrence).length > 0) ||
      Object.keys(restDiff ?? {}).length > 0;
    return !hasNonRecurrenceChanges;
  }, [currentDiff, formData, hasRecurrenceChanged]);

  const isStartDateTreatedAsChanged = useCallback(
    (nextData: EventFormData) => {
      const initial = initialFormDataRef.current;
      const initialTimeZone = initial.timeZone ?? eventData.time_zone;
      const nextTimeZone = nextData.timeZone ?? eventData.time_zone;
      return (
        initial.startDate !== nextData.startDate ||
        initialTimeZone !== nextTimeZone
      );
    },
    [eventData.time_zone],
  );

  const recurrenceScopeOptions = useMemo<RecurrenceScope[]>(() => {
    if (formData.recurrence || eventData.recurrence) {
      const startDateChanged = isStartDateTreatedAsChanged(formData);
      if (recurrenceOnlyChange) {
        return eventData.is_parent === true
          ? (["single", "future"] as RecurrenceScope[])
          : (["all"] as RecurrenceScope[]);
      }
      const options =
        eventData.is_parent === true
          ? (["single", "future"] as RecurrenceScope[])
          : (["single", "future", "all"] as RecurrenceScope[]);
      if (!startDateChanged) return options;
      const filtered = options.filter((option) => option !== "all");
      return filtered.length > 0 ? filtered : (["single"] as RecurrenceScope[]);
    }
    return ["single"] as RecurrenceScope[];
  }, [
    eventData.is_parent,
    eventData.recurrence,
    formData.recurrence,
    recurrenceOnlyChange,
    formData,
    isStartDateTreatedAsChanged,
  ]);

  const defaultScopeForModal = useMemo<RecurrenceScope>(() => {
    const preferred = (formData.scope ?? "single") as RecurrenceScope;
    return recurrenceScopeOptions.includes(preferred)
      ? preferred
      : recurrenceScopeOptions[0];
  }, [formData.scope, recurrenceScopeOptions]);

  const scopeModalDescription = useMemo(() => {
    const summary = formatRecurrencePattern(
      formData.recurrence ?? eventData.recurrence,
      t,
    );
    if (!summary || (!formData.recurrence && !eventData.recurrence))
      return undefined;
    return t("eventManage.scopeSelectionDescriptionWithRecurrence", {
      summary,
      range: "",
    });
  }, [eventData.recurrence, formData.recurrence, t]);

  const getScopedFormData = (scope?: RecurrenceScope): EventFormData => {
    if (!requiresScopeSelection) return { ...formData, scope: undefined };
    const scopeToUse = scope ?? formData.scope ?? "single";
    return { ...formData, scope: scopeToUse };
  };

  const maybeOpenScopeModal = (
    action: "save" | "backToDraft" | "publish",
    recomposeMode?: "until_extend" | "until_shrink",
  ) => {
    if (!requiresScopeSelection) return false;
    setPendingScopeAction(action);
    setPendingRecomposeMode(recomposeMode ?? null);
    setIsScopeModalOpen(true);
    return true;
  };

  const performAction = async (
    action: "save" | "backToDraft" | "publish",
    scope?: RecurrenceScope,
    recomposeMode?: "until_extend" | "until_shrink",
  ) => {
    if (action === "save") {
      await performSave(getScopedFormData(scope), { recomposeMode });
      return;
    }
    if (action === "publish") {
      await performSave(
        { ...getScopedFormData(scope), is_draft: 0, hidden: 0 },
        { recomposeMode },
      );
      return;
    }
    if (action === "backToDraft") {
      await performSave(
        { ...getScopedFormData(scope), is_draft: 1 },
        { skipPropagate: true, recomposeMode },
      );
    }
  };

  const handleScopeConfirm = async (scope: RecurrenceScope) => {
    const action = pendingScopeAction;
    const recomposeMode = pendingRecomposeMode ?? undefined;
    setIsScopeModalOpen(false);
    setPendingScopeAction(null);
    setPendingRecomposeMode(null);
    if (!action) return;

    await performAction(action, scope, recomposeMode);
  };

  const handleScopeCancel = () => {
    setIsScopeModalOpen(false);
    setPendingScopeAction(null);
    setPendingRecomposeMode(null);
  };

  const performSave = async (
    targetFormData: EventFormData,
    options?: {
      skipPropagate?: boolean;
      recomposeMode?: "until_extend" | "until_shrink";
    },
  ) => {
    const recurrenceChanged = hasRecurrenceChanged(targetFormData);
    try {
      const apiRequest = buildEventUpdateDiff(
        initialFormDataRef.current,
        targetFormData,
        eventData.time_zone,
      );
      const {
        CalendarEntryForm,
        CalendarEntry,
        scope: _scope,
        ...restRequest
      } = apiRequest;
      const formWithoutRecurrence =
        CalendarEntryForm && typeof CalendarEntryForm === "object"
          ? (() => {
              const {
                recurrence: _recurrence,
                scope: _scope,
                ...restForm
              } = CalendarEntryForm;
              return restForm;
            })()
          : undefined;
      const hasNonRecurrenceChanges =
        (CalendarEntry && Object.keys(CalendarEntry).length > 0) ||
        (formWithoutRecurrence &&
          Object.keys(formWithoutRecurrence).length > 0) ||
        Object.keys(restRequest).length > 0;

      // 繰り返しルール以外の変更がある場合、更新APIを呼ぶ: recurrenceはrecomposeで扱うため分離
      if (hasNonRecurrenceChanges) {
        await updateEvent(eventData.id, apiRequest);
      }
      // 繰り返しルールが変わった場合は再構成APIで子イベント群を更新
      // single を選んだ場合はこのイベントのみ対象なので再構成しない
      const startDateChanged = isStartDateTreatedAsChanged(targetFormData);
      const untilBeforeStart =
        Boolean(targetFormData.recurrence?.until) &&
        Boolean(targetFormData.startDate) &&
        dayjs(targetFormData.recurrence?.until).isValid() &&
        dayjs(targetFormData.startDate).isValid() &&
        dayjs(targetFormData.recurrence?.until).isBefore(
          dayjs(targetFormData.startDate),
          "day",
        );
      const shouldRecompose =
        (recurrenceChanged || startDateChanged) &&
        targetFormData.scope !== "single" &&
        !(hasNonRecurrenceChanges && untilBeforeStart);
      if (shouldRecompose) {
        const shouldRebuildFuture =
          targetFormData.scope === "future" && startDateChanged === true;
        await recomposeEvent(
          eventData.id,
          targetFormData.recurrence ?? null,
          targetFormData.scope ?? "single",
          options?.recomposeMode,
          shouldRebuildFuture,
        );
      }
      const shouldPropagate =
        !options?.skipPropagate &&
        options?.recomposeMode !== "until_shrink" &&
        targetFormData.recurrence &&
        (!wasRecurringInitially || targetFormData.recurrence !== null);
      if (shouldPropagate) {
        // 繰り返しイベントの場合のみ、全イベントへリマインダーを伝播するAPIを呼ぶ
        try {
          await propagateReminders(eventData.id, "all");
        } catch (propagateError) {
          console.error("Failed to propagate reminders:", propagateError);
        }
      }
      initialFormDataRef.current = targetFormData;
      setFormData(targetFormData);
      onSaveSuccess({ resetModal: shouldRecompose });
    } catch (error) {
      console.error("Error updating event:", error);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    const validation = validateForm();
    setValidationErrors(validation.errors);
    if (!validation.isValid) return;

    const untilMode = (() => {
      const prevUntilRaw = initialFormDataRef.current.recurrence?.until;
      const nextUntilRaw = formData.recurrence?.until;
      const prevUntil =
        prevUntilRaw && dayjs(prevUntilRaw).isValid()
          ? dayjs(prevUntilRaw).format("YYYY-MM-DD")
          : null;
      const nextUntil =
        nextUntilRaw && dayjs(nextUntilRaw).isValid()
          ? dayjs(nextUntilRaw).format("YYYY-MM-DD")
          : null;
      if (!prevUntil || !nextUntil) return undefined;
      if (nextUntil > prevUntil) return "until_extend" as const;
      if (nextUntil < prevUntil) return "until_shrink" as const;
      return undefined;
    })();

    if (maybeOpenScopeModal("save", untilMode)) return;

    await performAction("save", undefined, untilMode);
  };

  const handlePublish = async () => {
    if (isDraft) {
      formData.is_draft = 0;
    }
    const validation = validateForm();
    setValidationErrors(validation.errors);
    if (!validation.isValid) return;

    if (maybeOpenScopeModal("publish")) return;

    await performAction("publish");
  };

  const handleBackToDraft = async () => {
    formData.is_draft = 1;

    const validation = validateForm();
    setValidationErrors(validation.errors);
    if (!validation.isValid) return;

    if (maybeOpenScopeModal("backToDraft")) return;

    await performAction("backToDraft");
  };

  const dirtyCheck = useMemo(() => {
    // 終日でない場合に時刻が欠けるとdiffが計算できないため、変更ありとして扱う
    const missingTimes =
      !formData.isAllDay && (!formData.startTime || !formData.endTime);
    let diff: ReturnType<typeof buildEventUpdateDiff> | null = null;
    let diffError: Error | null = null;

    if (!missingTimes) {
      try {
        diff = buildEventUpdateDiff(
          initialFormDataRef.current,
          formData,
          eventData.time_zone,
        );
      } catch (error) {
        diffError = error as Error;
        console.warn("Failed to build diff in hasChanges", error);
      }
    }

    const recurrenceChanged = !areRecurrenceRulesEqual(
      initialFormDataRef.current.recurrence,
      formData.recurrence,
    );
    const diffHasChanges = Boolean(diff && Object.keys(diff).length > 0);
    return {
      hasChanges:
        missingTimes ||
        recurrenceChanged ||
        diffHasChanges ||
        Boolean(diffError),
      missingTimes,
      recurrenceChanged,
      diffHasChanges,
      diffError,
    };
  }, [formData, eventData.time_zone]);
  const hasChanges = dirtyCheck.hasChanges;
  const diffErrorMessage = useMemo(() => {
    if (!dirtyCheck.diffError) return null;
    return t(
      "eventEdit.diffCalculationFailed",
      "Unable to calculate changes. Please review date/time fields.",
    );
  }, [dirtyCheck.diffError, t]);

  const handleCancel = () => {
    if (confirm(t("common.cancelConfirm"))) {
      resetUpdate();
      onCancel();
    }
  };

  if (requiresScopeSelection && isScopeModalOpen) {
    return (
      <div className="flex h-full flex-col">
        <ScopeSelectionContent
          isActive={isScopeModalOpen}
          onClose={handleScopeCancel}
          onConfirm={handleScopeConfirm}
          defaultScope={defaultScopeForModal}
          scopes={recurrenceScopeOptions}
          loading={isMutating}
          description={scopeModalDescription}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <h2 className="text-lg font-medium text-rw-black-gray">
        {t("eventManage.editEvent")}
        {isDraft && (
          <span className="ml-2 text-sm font-normal text-orange-600">
            ({t("common.draft")})
          </span>
        )}
      </h2>

      {/* Form contents */}
      <div className="grid grid-cols-2 gap-3 overflow-y-auto">
        <div className="col-span-2">
          <div className="flex justify-between">
            <Label htmlFor={titleId}>{t("eventCreate.eventName")}*</Label>
            <span>
              {formData.title.length} / {EVENT_TITLE_MAX_LENGTH}
            </span>
          </div>
          <Input
            id={titleId}
            value={formData.title}
            onChange={handleTitleChange}
            maxLength={EVENT_TITLE_MAX_LENGTH}
            className={dirtyClass("title")}
            required
          />
          {validationErrors.title && (
            <p className="mt-1 text-red-500">{validationErrors.title}</p>
          )}
        </div>

        <div className="col-span-2">
          <div className="flex justify-between">
            <Label htmlFor={descriptionId}>{t("common.description")}</Label>
            <span>
              {formData.description?.length} / {EVENT_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
          <Textarea
            id={descriptionId}
            value={formData.description}
            onChange={handleDescriptionChange}
            maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
            rows={4}
            className={dirtyClass("description")}
          />
        </div>

        <div className="col-span-2">
          <Label>{t("common.location")}*</Label>

          <div className="mt-1 space-y-3">
            <div className="flex space-x-3">
              <label className="inline-flex cursor-pointer items-center">
                <input
                  type="radio"
                  name="locationType"
                  value={EVENT_LOCATION_TYPE_ONLINE}
                  checked={
                    formData.location_type === EVENT_LOCATION_TYPE_ONLINE
                  }
                  onChange={() =>
                    handleLocationTypeChange(EVENT_LOCATION_TYPE_ONLINE)
                  }
                  className={`rounded-full border-gray-300 text-rw-sub-deep focus:ring-rw-main ${dirtyClass("location_type")}`}
                />
                <span className="ml-2">{t("common.online")}</span>
              </label>

              <label className="inline-flex cursor-pointer items-center">
                <input
                  type="radio"
                  name="locationType"
                  value={EVENT_LOCATION_TYPE_HYBRID}
                  checked={
                    formData.location_type === EVENT_LOCATION_TYPE_HYBRID
                  }
                  onChange={() =>
                    handleLocationTypeChange(EVENT_LOCATION_TYPE_HYBRID)
                  }
                  className={`rounded-full border-gray-300 text-rw-sub-deep focus:ring-rw-main ${dirtyClass("location_type")}`}
                />
                <span className="ml-2">{t("common.hybrid")}</span>
              </label>

              <label className="inline-flex cursor-pointer items-center">
                <input
                  type="radio"
                  name="locationType"
                  value={EVENT_LOCATION_TYPE_OFFLINE}
                  checked={
                    formData.location_type === EVENT_LOCATION_TYPE_OFFLINE
                  }
                  onChange={() =>
                    handleLocationTypeChange(EVENT_LOCATION_TYPE_OFFLINE)
                  }
                  className={`rounded-full border-gray-300 text-rw-sub-deep focus:ring-rw-main ${dirtyClass("location_type")}`}
                />
                <span className="ml-2">{t("common.inPerson")}</span>
              </label>
            </div>

            {(formData.location_type === EVENT_LOCATION_TYPE_OFFLINE ||
              formData.location_type === EVENT_LOCATION_TYPE_HYBRID) && (
              <label
                htmlFor={inpersonLocationId}
                className="flex flex-col gap-1 text-xs"
              >
                <div className="flex justify-between">
                  {t("eventCreate.physicalLocation")}
                  <span className="text-xs">
                    {formData.location?.length || 0} /{" "}
                    {EVENT_LOCATION_MAX_LENGTH}
                  </span>
                </div>

                <Input
                  id={inpersonLocationId}
                  value={formData.location || ""}
                  onChange={handleInPersonLocationChange}
                  maxLength={EVENT_LOCATION_MAX_LENGTH}
                  placeholder={t("eventCreate.locationPlaceholder")}
                  className={dirtyClass("location")}
                />
              </label>
            )}

            {(formData.location_type === EVENT_LOCATION_TYPE_ONLINE ||
              formData.location_type === EVENT_LOCATION_TYPE_HYBRID) && (
              <label
                htmlFor={onlineLocationId}
                className="flex flex-col gap-1 text-xs"
              >
                <div className="flex justify-between">
                  {t("common.eventUrl")}
                  <span className="text-xs">
                    {formData.online_location?.length || 0} /{" "}
                    {EVENT_ONLINE_LOCATION_MAX_LENGTH}
                  </span>
                </div>
                <Input
                  id={onlineLocationId}
                  value={formData.online_location || ""}
                  onChange={handleOnlineLocationChange}
                  maxLength={EVENT_ONLINE_LOCATION_MAX_LENGTH}
                  placeholder={t("common.urlPlaceholder")}
                  className={dirtyClass("online_location")}
                />
              </label>
            )}
            {validationErrors.location && (
              <p className="mt-1 text-red-500">{validationErrors.location}</p>
            )}
          </div>
        </div>

        <div className="col-span-2">
          <TimeZoneFieldWithModal
            value={selectedTimeZone}
            options={timeZoneOptions}
            onChange={handleTimeZoneChange}
            className={dirtyClass("timeZone")}
          />
        </div>

        <div className="col-span-2">
          <EventDateSection
            eventData={formData}
            onChange={handleDateSectionChange}
            className="mt-4"
            showLabels
            dirtyClassNameFor={dirtyClass}
          />
          {validationErrors.startDate && (
            <p className="mt-1 text-red-500">{validationErrors.startDate}</p>
          )}
          {validationErrors.endDate && (
            <p className="mt-1 text-red-500">{validationErrors.endDate}</p>
          )}
          {validationErrors.startTime && (
            <p className="mt-1 text-red-500">{validationErrors.startTime}</p>
          )}
          {validationErrors.endTime && (
            <p className="mt-1 text-red-500">{validationErrors.endTime}</p>
          )}
          {validationErrors.dateTime && (
            <p className="mt-2 text-sm text-red-500">
              {validationErrors.dateTime}
            </p>
          )}
          {diffErrorMessage && (
            <p className="mt-2 text-sm text-red-500">{diffErrorMessage}</p>
          )}
        </div>

        <div className="col-span-2 space-y-2">
          <div className="flex items-center gap-2">
            <Switch
              id={recurrenceToggleId}
              checked={Boolean(formData.recurrence)}
              onCheckedChange={(checked) =>
                setFormData((prev) => {
                  if (checked) {
                    const defaultUntil = prev.endDate || prev.startDate || "";
                    return {
                      ...prev,
                      recurrence:
                        prev.recurrence ??
                        ({
                          freq: "DAILY",
                          interval: 1,
                          until: defaultUntil,
                        } as RecurrenceRule),
                    };
                  }
                  return { ...prev, recurrence: null };
                })
              }
            />
            <Label htmlFor={recurrenceToggleId} className="text-sm">
              {t("common.enableRecurrence", "Enable recurrence")}
            </Label>
          </div>

          {formData.recurrence && (
            <RecurrenceEditor
              value={formData.recurrence}
              startDate={formData.startDate}
              endDate={formData.endDate}
              minEndDate={minRecurrenceUntilDate}
              onChange={(rule) =>
                setFormData((prev) => ({ ...prev, recurrence: rule }))
              }
              className={dirtyClass("recurrence")}
            />
          )}
        </div>

        <div>
          <Label htmlFor={isPublicId}>
            {t("eventManage.eventVisibility")}*
          </Label>
          <Select
            value={formData.is_public.toString()}
            onValueChange={(value) =>
              handleFormChange("is_public", Number.parseInt(value, 10))
            }
            disabled={isPrivateGroup}
          >
            <SelectTrigger className={dirtyClass("is_public")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1" disabled={isPrivateGroup}>
                {t("eventManage.loungeWide")}
              </SelectItem>
              <SelectItem value="0">
                {t("eventManage.studyGroupOnly")}
              </SelectItem>
            </SelectContent>
          </Select>
          {isPrivateGroup && (
            <p className="mt-1 text-xs text-yellow-600">
              {t("eventManage.loungeWideNotAvailable")}
            </p>
          )}
          {validationErrors.is_public && (
            <p className="mt-1 text-red-500">{validationErrors.is_public}</p>
          )}
        </div>

        <div>
          <Label htmlFor={languageId}>{t("common.language")}*</Label>
          <Select
            value={formData.language}
            onValueChange={(value) => handleFormChange("language", value)}
          >
            <SelectTrigger className={dirtyClass("language")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={LANG_EN}>{t("common.english")}</SelectItem>
              <SelectItem value={LANG_JP}>{t("common.japanese")}</SelectItem>
            </SelectContent>
          </Select>
          {validationErrors.language && (
            <p className="mt-1 text-red-500">{validationErrors.language}</p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="w-full text-center">
        <div className="flex justify-between">
          <Button
            variant="ghost"
            size="sm"
            shaped
            onClick={handleCancel}
            disabled={updateLoading}
          >
            {t("common.cancel")}
          </Button>

          <div className="flex gap-3">
            {!isDraft && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  shaped
                  onClick={handleBackToDraft}
                  disabled={isMutating}
                >
                  {t("common.backToDraft")}
                </Button>

                <Button
                  variant="teal"
                  size="sm"
                  shaped
                  onClick={handleSave}
                  disabled={isMutating || !hasChanges}
                >
                  {t("common.saveChanges")}
                </Button>
              </>
            )}

            {isDraft && (
              <>
                <Button
                  variant="teal"
                  size="sm"
                  shaped
                  onClick={handleSave}
                  disabled={isMutating || !hasChanges}
                >
                  {t("common.updateDraft")}
                </Button>

                <Button
                  variant="teal"
                  size="sm"
                  shaped
                  onClick={handlePublish}
                  disabled={isMutating}
                >
                  {t("eventManage.publishEvent")}
                </Button>
              </>
            )}
          </div>
        </div>
        {updateError && (
          <p className="mt-2 text-sm text-red-500">
            {t("common.error")}: {updateError}
          </p>
        )}
      </div>
    </div>
  );
};
